'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { useTrackEvent } from '@/lib/posthog';

import { useSubscribe } from './use-subscribe';
import { useSubscribeModalTrigger } from './use-subscribe-modal-trigger';

export function SubscribeModal() {
  const [open, setOpen] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const lastFocused = useRef<HTMLElement | null>(null);
  const { trackEvent } = useTrackEvent();

  const reveal = useCallback(() => {
    lastFocused.current = document.activeElement as HTMLElement | null;
    setOpen(true);
    trackEvent('SUBSCRIBE_MODAL_SHOWN', { trigger: 'exit_intent' }, { source: 'client' });
  }, [trackEvent]);

  // Exit-intent trigger + once-per-visitor gating live in the hook.
  useSubscribeModalTrigger(reveal);

  const close = useCallback(() => {
    setOpen(false);
    lastFocused.current?.focus?.();
    trackEvent('SUBSCRIBE_MODAL_DISMISSED', {}, { source: 'client' });
  }, [trackEvent]);

  // Esc + focus trap + initial focus.
  useEffect(() => {
    if (!open) return;
    const node = dialogRef.current;
    const focusables = node
      ? Array.from(
          node.querySelectorAll<HTMLElement>(
            'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])'
          )
        )
      : [];
    focusables[0]?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        close();
        return;
      }
      if (e.key !== 'Tab' || focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (!first || !last) return; // noUncheckedIndexedAccess: narrow before .focus()
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, close]);

  if (!open) return null;
  return <ModalBody dialogRef={dialogRef} onClose={close} />;
}

function ModalBody({
  dialogRef,
  onClose,
}: {
  dialogRef: React.RefObject<HTMLDivElement | null>;
  onClose: () => void;
}) {
  const { status, error, submit } = useSubscribe('modal');
  const [email, setEmail] = useState('');
  const [hp, setHp] = useState('');

  return (
    <div className="subscribe-overlay" onClick={onClose} role="presentation">
      <div
        ref={dialogRef}
        className="subscribe-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="subscribe-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="subscribe-close" aria-label="Close" onClick={onClose}>
          ×
        </button>
        <div className="eyebrow">Before you go</div>
        <h3
          id="subscribe-modal-title"
          className="display"
          style={{ fontSize: 30, lineHeight: 1.1, margin: '0 0 14px' }}
        >
          Want guides, offers &amp; updates?
        </h3>
        {status === 'pending' ? (
          <p className="subscribe-success" role="status" style={{ marginBottom: 8 }}>
            Check your inbox to confirm.
          </p>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void submit(email, hp);
            }}
            noValidate
          >
            <p className="lede" style={{ margin: '0 0 20px' }}>
              Occasional emails — helpful guides for capturing a life, product news, and the odd
              offer. No spam, unsubscribe anytime.
            </p>
            <input
              type="email"
              className="subscribe-input"
              placeholder="you@email.com"
              aria-label="Email address"
              aria-invalid={status === 'error'}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{ width: '100%', marginBottom: 10 }}
            />
            <input
              className="subscribe-hp"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              value={hp}
              onChange={(e) => setHp(e.target.value)}
            />
            <button
              type="submit"
              className="btn btn-primary"
              style={{ width: '100%', justifyContent: 'center' }}
              disabled={status === 'submitting'}
            >
              {status === 'submitting' ? 'Sending…' : 'Keep me posted'}
            </button>
            {status === 'error' && error && (
              <p className="subscribe-error" style={{ marginTop: 8 }}>
                {error}
              </p>
            )}
            <p className="foot-about-body" style={{ fontSize: 12, marginTop: 12 }}>
              By subscribing you agree to our <a href="/policies/privacy">Privacy Policy</a>.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
